import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, PenLine, MessageSquare, Inbox, History, Zap, Flame, MapPin } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { API_URL, LEAD_STATUS, companyLabel, fmtDT } from './c360Shared';

const Row = ({ children, testId }) => (
  <div className="flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1 py-2.5 border-b border-slate-100 last:border-0 text-sm" data-testid={testId}>{children}</div>
);
const Empty = ({ t }) => <div className="py-6 text-center text-sm text-slate-400">{t}</div>;

const AltRow = ({ a, i }) => (
  <Row testId={`c360-alt-${i}`}>
    <span className="text-xs text-slate-500 whitespace-nowrap w-28 shrink-0">{fmtDT(a.at)}</span>
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${a.tipo === 'contrato' ? 'bg-[#2D3E6F] text-white' : 'bg-amber-100 text-amber-800'}`}>{a.tipo === 'contrato' ? 'Contrato' : 'Lead'}</span>
    <Link to={a.tipo === 'contrato' ? `/contracts/${a.service_category || 'luz_gas'}/${a.company}/${a.contract_id}` : `/leads/${a.lead_id}`} className="flex-1 hover:underline">
      {a.tipo === 'contrato' && <b>{a.codigo ? `${a.codigo} · ` : ''}{companyLabel(a.company)}: </b>}{a.texto}
    </Link>
    <span className="text-xs text-slate-500 whitespace-nowrap">{a.por || ''}</span>
  </Row>
);

const Bloco = ({ title, sub, items, testId, icon: Icon }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
    <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
      {Icon && <Icon className="w-4 h-4 text-[#2D3E6F] shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 break-all">{title}</p>
        {sub && <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{sub}</p>}
      </div>
      <span className="text-xs text-slate-500 whitespace-nowrap">{items.length} registo(s)</span>
    </div>
    <div className="px-3 max-h-[360px] overflow-y-auto">
      {items.length ? items.map((a, i) => <AltRow key={i} a={a} i={`${testId}-${i}`} />) : <Empty t="Sem alterações." />}
    </div>
  </div>
);

const Alteracoes = ({ data }) => {
  const [porLocal, setPorLocal] = React.useState(true);
  const locais = data.locais || [];
  const alts = data.alteracoes;
  const toggle = locais.length > 0 && (
    <div className="flex gap-1.5 pt-2" data-testid="c360-alt-toggle">
      {[[true, 'Por CPE / CUI'], [false, 'Tudo por data']].map(([v, l]) => (
        <button key={l} type="button" onClick={() => setPorLocal(v)} data-testid={`c360-alt-mode-${v ? 'local' : 'data'}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${porLocal === v ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
      ))}
    </div>
  );
  if (!locais.length || !porLocal) {
    return <>{toggle}<div className="max-h-[480px] overflow-y-auto">{alts.map((a, i) => <AltRow key={i} a={a} i={i} />)}</div></>;
  }
  const outros = alts.filter((a) => a.tipo === 'contrato' && !a.local_key);
  const leads = alts.filter((a) => a.tipo === 'lead' && !a.local_key);
  return (
    <>
      {toggle}
      <div className="space-y-3 pt-2" data-testid="c360-alt-por-local">
        {locais.map((l, idx) => alts.some((a) => a.local_key === l.key) && (
          <Bloco key={l.key} testId={`c360-local-${idx}`} icon={l.cpe ? Zap : Flame}
            title={[l.cpe && `CPE ${l.cpe}`, l.cui && `CUI ${l.cui}`].filter(Boolean).join(' · ')}
            sub={[l.morada, l.localidade].filter(Boolean).join(', ') || null}
            items={alts.filter((a) => a.local_key === l.key)} />
        ))}
        {outros.length > 0 && <Bloco testId="c360-local-outros" icon={FileText} title="Outros serviços (telecom, alarmes…)" items={outros} />}
        {leads.length > 0 && <Bloco testId="c360-local-leads" icon={Inbox} title="Leads e contactos" items={leads} />}
      </div>
    </>
  );
};

export const ClientHistory = ({ data }) => {
  const token = localStorage.getItem('token');
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="c360-history">
      <h2 className="text-base md:text-lg font-bold font-[Outfit] text-[#2D3E6F] mb-2">Histórico</h2>
      <Tabs defaultValue="alteracoes">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="alteracoes" data-testid="c360-tab-alteracoes"><History className="w-3.5 h-3.5 mr-1" />Alterações ({(data.alteracoes || []).length})</TabsTrigger>
          <TabsTrigger value="leads" data-testid="c360-tab-leads"><Inbox className="w-3.5 h-3.5 mr-1" />Leads ({data.leads.length})</TabsTrigger>
          <TabsTrigger value="contactos" data-testid="c360-tab-contactos"><MessageSquare className="w-3.5 h-3.5 mr-1" />Contactos ({data.contactos.length})</TabsTrigger>
          <TabsTrigger value="assinaturas" data-testid="c360-tab-assinaturas"><PenLine className="w-3.5 h-3.5 mr-1" />Assinaturas ({data.assinaturas.length})</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="c360-tab-documentos"><FileText className="w-3.5 h-3.5 mr-1" />Documentos ({data.documentos.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="alteracoes">
          {!(data.alteracoes || []).length ? <Empty t="Sem alterações registadas." /> : <Alteracoes data={data} />}
        </TabsContent>
        <TabsContent value="leads">
          {data.leads.length === 0 ? <Empty t="Sem leads." /> : data.leads.map((l) => (
            <Row key={l.id} testId={`c360-lead-${l.id}`}>
              <Link to={`/leads/${l.id}`} className="flex-1 hover:text-[#2D3E6F] hover:underline">
                <b>{LEAD_STATUS[l.status] || l.status}</b>{l.service_type ? ` · ${l.service_type}` : ''}{l.source ? ` · ${l.source}` : ''}
              </Link>
              <span className="text-xs text-slate-500 whitespace-nowrap">{l.assigned_to_name || 'Por atribuir'} · {fmtDT(l.created_at)}</span>
            </Row>
          ))}
        </TabsContent>
        <TabsContent value="contactos">
          {data.contactos.length === 0 ? <Empty t="Sem contactos registados." /> : <div className="max-h-[480px] overflow-y-auto">{data.contactos.map((c, i) => (
            <Row key={c.id || i} testId={`c360-contact-${i}`}>
              <span className="flex-1">{c.notes || `${c.method || ''} · ${c.outcome || ''}`}</span>
              <span className="text-xs text-slate-500 whitespace-nowrap">{c.created_by_name} · {fmtDT(c.created_at)}</span>
            </Row>
          ))}</div>}
        </TabsContent>
        <TabsContent value="assinaturas">
          {data.assinaturas.length === 0 ? <Empty t="Sem pedidos de assinatura." /> : data.assinaturas.map((s) => (
            <Row key={s.id} testId={`c360-sig-${s.id}`}>
              <span className="flex-1"><b>{s.document_name}</b> · {companyLabel(s.empresa)}</span>
              <span className={`text-xs font-semibold ${s.status === 'assinado' ? 'text-emerald-700' : 'text-amber-700'}`}>{s.status === 'assinado' ? `Assinado ${fmtDT(s.signed_at)}` : 'A aguardar assinatura'}</span>
            </Row>
          ))}
        </TabsContent>
        <TabsContent value="documentos">
          {data.documentos.length === 0 ? <Empty t="Sem documentos." /> : data.documentos.map((d) => (
            <Row key={d.id} testId={`c360-doc-${d.id}`}>
              <a href={`${API_URL}/files/${d.id}/download?auth=${token}`} target="_blank" rel="noreferrer" className="flex-1 text-[#2D3E6F] hover:underline break-all">{d.filename}</a>
              <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDT(d.created_at)}</span>
            </Row>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};
